// src/components/ToolPicker.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../config/supabase';

const { FiLoader, FiAlertCircle, FiX } = FiIcons;

const ToolPicker = ({ noteId, studentId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tools, setTools] = useState([]);
  const [insertingTool, setInsertingTool] = useState(null);

  useEffect(() => {
    fetchAvailableTools();
  }, []);

  const fetchAvailableTools = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .eq('visibility', 'visible')
        .order('type', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      
      if (data) {
        setTools(data);
      } else {
        setTools([]);
      }
    } catch (err) {
      console.error('Error fetching available tools:', err);
      setError('Failed to load available tools');
    } finally {
      setLoading(false);
    }
  };

  const handleToolSelect = async (tool) => {
    if (!noteId || !studentId) {
      setError('Missing required information');
      return;
    }
    
    try {
      setInsertingTool(tool.id);

      // Cheaper: count rows for this note and use count + 1
        const { count, error: orderError } = await supabase
        .from('lesson_tools')
        .select('id', { count: 'exact', head: true })
        .eq('lesson_note_id', noteId);
        
        if (orderError) throw orderError;
        const nextOrder = (count ?? 0) + 1;

      // Save all relevant tool data in tool_config to avoid needing to refetch it
      const toolConfig = {
        title: tool.name,
        icon_url: tool.icon_url,
        slug: tool.slug
      };

      // Insert the tool
  const { data: insertData, error: insertError } = await supabase
    .from('lesson_tools')
    .insert({
      lesson_note_id: noteId,
      student_id: studentId,         // ← NEW: set the student here
      tool_id: tool.id,
      tool_config: toolConfig,
      insert_order: nextOrder
    })
    .select();

      if (insertError) throw insertError;

      if (insertData && insertData.length > 0) {
        const lessonToolId = insertData[0].id;
        console.log(`Tool inserted successfully. ID: ${lessonToolId}, Tool ID: ${tool.id}`);
        // Notify parent that tool has been added successfully
        onClose(lessonToolId, tool.id);
      }
    } catch (err) {
      console.error(`Error inserting ${tool.name} tool:`, err);
      setError(`Failed to add ${tool.name}`);
      setInsertingTool(null);
    }
  };

  const getIconUrl = (tool) => {
    // Return the icon_url if it exists, otherwise return a default icon
    return tool.icon_url || 'https://via.placeholder.com/24';
  };

  // Group tools by type
  const toolGroups = useMemo(() => {
    if (!tools || tools.length === 0) return {};

    // Group tools by their type
    return tools.reduce((acc, tool) => {
      // Use "Other" as the default category if type is missing
      const type = tool.type || "Other";
      
      if (!acc[type]) {
        acc[type] = [];
      }
      
      acc[type].push(tool);
      return acc;
    }, {});
  }, [tools]);

  // Sort group names alphabetically
  const sortedGroupNames = useMemo(() => {
    return Object.keys(toolGroups).sort((a, b) => {
      // Put "Other" at the end
      if (a === "Other") return 1;
      if (b === "Other") return -1;
      return a.localeCompare(b);
    });
  }, [toolGroups]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose && onClose()}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -20, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Add a Tool</h2>
            {/* Subheader / hardcoded note */}
            <p className="text-sm text-gray-600 mt-1">
              Audio recorder and more tools coming soon...
            </p>
          </div>
          <button
            onClick={() => onClose()}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <div className="flex">
              <SafeIcon icon={FiAlertCircle} className="text-red-500" />
              <p className="ml-3 text-red-700">{error}</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <SafeIcon icon={FiLoader} className="text-blue-500 text-2xl" />
            </motion.div>
            <span className="ml-2 text-gray-600">Loading available tools...</span>
          </div>
        ) : tools.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No tools available</p>
        ) : (
          <div className="space-y-6">
            {sortedGroupNames.map(groupName => (
              <div key={groupName} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                <h3 className="font-medium text-gray-900 mb-3 text-base">{groupName}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {toolGroups[groupName].map((tool) => (
                    <motion.button
                      key={tool.id}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-all"
                      onClick={() => handleToolSelect(tool)}
                      disabled={insertingTool !== null}
                      title={tool.description || tool.name}
                    >
                      {insertingTool === tool.id ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="mb-2"
                        >
                          <SafeIcon icon={FiLoader} className="text-blue-500 text-2xl" />
                        </motion.div>
                      ) : (
                        <img
                          src={getIconUrl(tool)}
                          alt={`${tool.name} icon`}
                          className="w-10 h-10 mb-2 object-contain"
                          onError={(e) => {
                            e.target.onerror = null;
                            // Use a default icon from react-icons if the image fails to load
                            e.target.style.display = 'none';
                            const parent = e.target.parentNode;
                            if (parent) {
                              const iconEl = document.createElement('div');
                              iconEl.className = 'text-blue-500 text-2xl mb-2';
                              // Add a default icon based on the tool slug
                              const iconComponent = getToolIconBySlug(tool.slug);
                              parent.insertBefore(iconEl, parent.firstChild);
                            }
                          }}
                        />
                      )}
                      <span className="text-sm font-medium text-gray-700 text-center">{tool.name}</span>
                    </motion.button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Helper function to get icon by tool slug
const getToolIconBySlug = (slug) => {
  switch (slug) {
    case 'metronome':
      return FiIcons.FiMusic;
    case 'recorder':
      return FiIcons.FiMic;
    case 'piano':
      return FiIcons.FiGrid;
    case 'tuner':
      return FiIcons.FiActivity;
    default:
      return FiIcons.FiTool;
  }
};

export default ToolPicker;